import { useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import { useDesktopSlides } from "@/hooks/useIsDesktop";
import useSlickArrows from "@/hooks/useSlickArrows";
import styles from "./FeaturedPackage.module.css";
import { FaStar } from "react-icons/fa6";
import { LuWallet } from "react-icons/lu";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import PackageCountdown from "@/Component/Common/PackageCountdown";
import { getReviewCount, getSeatsLeftLabel } from "@/utils/packageCardMeta";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchFeaturedJourney } from "@/store/slices/holidaySlice";
import { formatIndianPrice } from "@/utils/formatIndianPrice";
import { isIndianUser } from "@/utils/getCountryCode";
import { withRegionPath } from "@/utils/session";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import { map } from "lodash";
import { INCLUSION_ICONS } from "@/utils/Inclusions";

const handleRouteRegion = (path) => {
  return isIndianUser() ? `/in${path}` : path
}
function PackageCard({ item }) {
  const nights = item.nights
  const inclusions = {
    flight: Boolean(item.isFlightIncluded),
    meal: Boolean(item.isMealsIncluded),
    transfer: Boolean(item.isTransferIncluded),
    hotel: Boolean(item.isHotelIncluded),
    visa: Boolean(item.isVisaIncluded),
    activity: Boolean(item.isActivityIncluded),
  }
  const activeInclusions = INCLUSION_ICONS.filter(({ id }) => inclusions?.[id]);
  const href = handleRouteRegion(`/holiday/${item.slug}`);
  const seatsLeft = getSeatsLeftLabel(item);
  const reviewCount = getReviewCount(item);

  return (
    <article className={styles.card}>
      <a href={href} className={styles.imageLink} aria-label={item.packageName || item.title}>
        <div className={styles.imageWrap}>
          <img src={item.imagePath} alt={item.title} className={styles.cardImage} />

          {item.tagOnThumbnail && (
            <span className={styles.badgeBest}>
              <FaStar style={{ marginTop: "-2px" }} />
              {item.tagOnThumbnail}
            </span>
          )}
          {seatsLeft ? <span className={styles.badgeSeats}>{seatsLeft}</span> : null}
          {item.badge?.type === "emi" && (
            <span className={styles.badgeEmi}>
              <LuWallet style={{ marginTop: "-2px" }} />
              {item.badge.label} &nbsp;·&nbsp; {item.badge.month}
            </span>
          )}

          {nights ? <span className={styles.duration}>{nights}N / {nights + 1}D</span> : null}
          {item.rating ? (
            <span className={styles.rating}>
              <strong style={{ color: "#0B192C", marginTop: "2px" }}>{item.rating}</strong>
              <FaStar color="#FFB835" />
              {reviewCount != null ? ` (${reviewCount})` : null}
            </span>
          ) : null}
        </div>
      </a>

      <div className={styles.body}>
        <h3 className={styles.cardTitle}>
          <a href={href} className={styles.titleLink}>{item.packageName}</a>
        </h3>
        <p className={styles.cardSubtitle}>{map(item.cities, city => `${city.nights}N ${city.city.cityName}`).join(" | ")}</p>
        <div className={styles.cardIncludes}>
          <div className={styles.inclusion}>
            {map(activeInclusions, inclusions => (
              <span className={styles.includeIcon}>
                {(inclusions.id === "flight" && item.flightSubText) ?
                  <span className={styles.flightSubTextTip}>{item.flightSubText}</span>              :
                  null
                }
                {inclusions.icon}
              </span>
            ))}
          </div>
          <PackageCountdown item={item} className={styles.cardTimer} />
        </div>
        <div className={styles.footer}>
          <span className={styles.discountTag}>{item.discountTag}</span>
          <div className={styles.footerContent}>
            <div className={styles.priceBlock}>
              <p className={styles.starting}>
                Starting From
                <span className={styles.oldPrice}>{getCurrencySymbol("sub", 10)}{formatIndianPrice(item.displayCutPrice)}</span>
              </p>
              <p className={styles.price}>
                {getCurrencySymbol("main", 16)}{formatIndianPrice(item.price)}
                <span className={styles.perPerson}>/Person</span>
              </p>
            </div>
            <a href={href} className={styles.viewDealsBtn}>
              View Deal
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function FeaturedPackage({ initialFeaturedJourney = null }) {
  const dispatch = useAppDispatch();
  const { isDesktop, mounted, slidesToShow } = useDesktopSlides();
  const sliderRef = useRef(null);
  const { featuredJourney: storeFeatured, loading } = useAppSelector((state) => state.holiday);
  const featuredJourney = storeFeatured || initialFeaturedJourney;

  useEffect(() => {
    if (initialFeaturedJourney) return;
    dispatch(fetchFeaturedJourney());
  }, [dispatch, initialFeaturedJourney]);

  const data = useMemo(() => {
    const list = featuredJourney?.holidayList;
    if (Array.isArray(list) && list.length) {
      return list;
    }
    if (!loading || initialFeaturedJourney) return [];
    return [];
  }, [featuredJourney, loading, initialFeaturedJourney]);

  const journeyMeta = featuredJourney?.data || featuredJourney || {};
  const sectionTitle = journeyMeta.title || "Featured Journeys";
  const sectionSubtitle =
    journeyMeta.subTitle || "Holidays our experts actually took";
  const viewAllHref =
    journeyMeta.viewAllUrl || withRegionPath("/holiday/list");

  const { canPrev, canNext, slickArrowEvents } = useSlickArrows(
    data?.length || 0,
    slidesToShow
  );

  const slickSettings = {
    dots: false,
    infinite: false,
    centerMode: false,
    speed: 400,
    slidesToShow,
    slidesToScroll: 1,
    arrows: false,
    swipe: false,
    draggable: false,
    touchMove: false,
    ...slickArrowEvents,
  };

  const showSlick = mounted && isDesktop;
  const fewSlides = data?.length <= slidesToShow;
  const isLoaded = Boolean(initialFeaturedJourney) || !loading;
  const hasItems = data.length > 0;
  const showEmpty = isLoaded && !hasItems;

  return (
    <section className={`${styles.section} standard-padding`}>
      <div className={styles.header}>
        <div className={styles.headingBlock}>
          <h2 className={"standard-heading"}>{sectionTitle}</h2>
          <p className={"standard-subheading"}>{sectionSubtitle}</p>
        </div>

        {hasItems ? (
          <div className="headerArrows">
            <button
              type="button"
              className="headerArrowBtn"
              aria-label="Previous"
              disabled={!canPrev}
              onClick={() => {
                if (typeof sliderRef.current?.slickPrev === "function") {
                  sliderRef.current.slickPrev();
                }
              }}
            >
              <MdChevronLeft size={22} />
            </button>
            <button
              type="button"
              className="headerArrowBtn"
              aria-label="Next"
              disabled={!canNext}
              onClick={() => {
                if (typeof sliderRef.current?.slickNext === "function") {
                  sliderRef.current.slickNext();
                }
              }}
            >
              <MdChevronRight size={22} />
            </button>
          </div>
        ) : null}
      </div>

      {showEmpty ? (
        <p className="nodata">No data found</p>
      ) : hasItems && showSlick ? (
        <div className={`${styles.slickWrap} ${fewSlides ? styles.alignLeft : ""}`}>
          <Slider ref={sliderRef} {...slickSettings}>
            {data?.map((item) => (
              <div key={item.packageId || item.id} className={styles.slickSlide}>
                <PackageCard item={item} />
              </div>
            ))}
          </Slider>
        </div>
      ) : hasItems ? (
        <div className={styles.slider}>
          {data?.map((item) => (
            <PackageCard key={item.packageId || item.id} item={item} />
          ))}
        </div>
      ) : null}
    </section>
  );
}
